import { useEffect, useState } from 'react';
import { isProductionEnvironment } from '@/utils/devTools';

interface EnvironmentInfo {
    protocol: 'http' | 'https';
    hostname: string;
    port: string;
    isSecure: boolean;
    isDevelopment: boolean;
    canUpgrade: boolean;
}

/**
 * Hook to get current environment information and provide upgrade options
 */
export const useEnvironmentInfo = (): EnvironmentInfo & {
    upgradeToHttps: () => void;
    downgradeToHttp: () => void;
} => {
    const [envInfo, setEnvInfo] = useState<EnvironmentInfo>({
        protocol: 'http',
        hostname: 'localhost',
        port: '3000',
        isSecure: false,
        isDevelopment: true,
        canUpgrade: false
    });

    useEffect(() => {
        const location = window.location;
        const protocol = location.protocol.replace(':', '') as 'http' | 'https';
        const hostname = location.hostname;
        const port = location.port;
        const isSecure = protocol === 'https';
        // Single source of truth for "is this a deployed build" — see utils/devTools.ts. The
        // upgrade/downgrade buttons below build an absolute URL from a hardcoded dev port (3000/3001),
        // which is wrong for any real deployed host — this must never evaluate true off a developer's
        // own machine.
        const isDevelopment = !isProductionEnvironment();

        // Determine if we can upgrade to HTTPS
        const canUpgrade = !isSecure && isDevelopment;

        setEnvInfo({
            protocol,
            hostname,
            port,
            isSecure,
            isDevelopment,
            canUpgrade
        });
    }, []);

    const upgradeToHttps = () => {
        const httpsPort = import.meta.env.VITE_HTTPS_PORT || '3001';
        const httpsUrl = `https://${envInfo.hostname}:${httpsPort}${window.location.pathname}${window.location.search}${window.location.hash}`;
        
        window.location.replace(httpsUrl);
    };

    const downgradeToHttp = () => {
        const httpPort = import.meta.env.VITE_HTTP_PORT || '3000';
        const httpUrl = `http://${envInfo.hostname}:${httpPort}${window.location.pathname}${window.location.search}${window.location.hash}`;
        
        window.location.replace(httpUrl);
    };

    return {
        ...envInfo,
        upgradeToHttps,
        downgradeToHttp
    };
};

/**
 * Component that displays environment info and upgrade options in development
 */
export const EnvironmentSwitcher: React.FC<{ 
    show?: boolean;
    position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
}> = ({ 
    show = true, 
    position = 'bottom-right' 
}) => {
    const envInfo = useEnvironmentInfo();

    if (!show || !envInfo.isDevelopment) {
        return null;
    }

    const positionClasses = {
        'top-left': 'top-4 left-4',
        'top-right': 'top-4 right-4',
        'bottom-left': 'bottom-4 left-4',
        'bottom-right': 'bottom-4 right-4'
    };

    return (
        <div className={`fixed ${positionClasses[position]} z-50 bg-white/90 backdrop-blur-sm border border-gray-200 rounded-lg p-3 shadow-lg text-sm`}>
            <div className="flex items-center gap-2 mb-2">
                <div className={`w-2 h-2 rounded-full ${envInfo.isSecure ? 'bg-green-500' : 'bg-yellow-500'}`} />
                <span className="font-medium">
                    {envInfo.protocol.toUpperCase()}
                </span>
                <span className="text-gray-500">
                    :{envInfo.port}
                </span>
            </div>
            
            {envInfo.canUpgrade && (
                <button
                    onClick={envInfo.upgradeToHttps}
                    className="w-full bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded text-xs transition-colors"
                    title="Switch to HTTPS for testing secure authentication"
                >
                    ?? Upgrade to HTTPS
                </button>
            )}
            
            {envInfo.isSecure && envInfo.isDevelopment && (
                <button
                    onClick={envInfo.downgradeToHttp}
                    className="w-full bg-gray-500 hover:bg-gray-600 text-white px-3 py-1 rounded text-xs transition-colors"
                    title="Switch back to HTTP development"
                >
                    ?? Back to HTTP
                </button>
            )}
        </div>
    );
};

export default EnvironmentSwitcher;